'use client';

import axios from '@/config/axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEntraAuth } from './useEntraAuth';
import { consumePendingAccountSelection } from '../../utils/pendingAccountSelection';
import { invalidateWebsiteSession } from '@/utils/clientSessionState';

export type CustomerAccount = {
    id: number;
    name: string;
    isPrimary: boolean;
};

export type AccountsState = {
    accounts: CustomerAccount[];
    selectedAccount: CustomerAccount | null;
    selectedAccountId: number | null;
    isPrimaryAccount: boolean;
    isLoading: boolean;
    isReady: boolean;
    errorStatus: number | null;
    hasAccounts: boolean;
    fetchAccounts: () => Promise<void>;
    selectAccount: (accountId: number | null) => Promise<void>;
};

export const useAccounts = (): AccountsState => {
    const { hasSession, isAuthenticated, isInitialized } = useEntraAuth();
    const [accounts, setAccounts] = useState<CustomerAccount[]>([]);
    const [selectedAccountId, setSelectedAccountIdState] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorStatus, setErrorStatus] = useState<number | null>(null);
    // Refs let fetchAccounts read latest values without them appearing in the dep array,
    // preventing re-fetch loops when selectedAccountId changes after an account selection.
    const selectedAccountIdRef = useRef<number | null>(null);
    useEffect(() => {
        selectedAccountIdRef.current = selectedAccountId;
    }, [selectedAccountId]);
    const sessionRestoredRef = useRef(false);

    const normalizeAccountId = useCallback((id: unknown): number | null => {
        if (typeof id === 'number' && Number.isInteger(id)) {
            return id;
        }

        if (typeof id === 'string' && id.trim()) {
            const parsed = Number(id);
            return Number.isInteger(parsed) ? parsed : null;
        }

        return null;
    }, []);

    const establishSession = useCallback(async (accountId: number) => {
        await axios.post('/api/sessions', { id: String(accountId) });
    }, []);

    const selectAccount = useCallback(async (accountId: number | null) => {
        if (accountId === null) {
            setSelectedAccountIdState(null);
            return;
        }

        await establishSession(accountId);
        setSelectedAccountIdState(accountId);
    }, [establishSession]);

    const resetAccountState = useCallback(() => {
        setAccounts([]);
        setSelectedAccountIdState(null);
        setErrorStatus(null);
        setIsLoading(false);
    }, []);

    const fetchAccounts = useCallback(async () => {
        if (!isInitialized) {
            return;
        }

        if (!isAuthenticated) {
            resetAccountState();
            return;
        }

        setIsLoading(true);
        setErrorStatus(null);

        try {
            const { data } = await axios.get<Array<{ id: string | number; name: string; isPrimary: boolean }>>(
                '/api/accounts'
            );

            const normalizedAccounts: CustomerAccount[] = data
                .map(account => {
                    const normalizedId = normalizeAccountId(account.id);
                    if (normalizedId === null) {
                        return null;
                    }

                    return {
                        id: normalizedId,
                        name: account.name,
                        isPrimary: account.isPrimary,
                    };
                })
                .filter((account): account is CustomerAccount => account !== null);

            setAccounts(normalizedAccounts);

            let effectiveSelectedId = selectedAccountIdRef.current;

            // On page reload selectedAccountId is null and session hasn't been restored yet.
            // A server session cookie may already hold the previously selected account — restore
            // it to avoid an unnecessary redirect to the account selector. Only attempt once.
            if (effectiveSelectedId === null && !sessionRestoredRef.current) {
                sessionRestoredRef.current = true;
                try {
                    const { data } = await axios.get<{ accountId: string }>('/api/sessions', { skipAuth: true });
                    effectiveSelectedId = normalizeAccountId(data.accountId);
                } catch {
                    // No active session or request failed — proceed to auto-select.
                }
            }

            const selectedAccount =
                effectiveSelectedId === null
                    ? null
                    : normalizedAccounts.find(account => account.id === effectiveSelectedId) ?? null;

            if (selectedAccount) {
                if (selectedAccountIdRef.current !== null) {
                    // Local state already has a selection (e.g. account switch) — re-establish session.
                    await selectAccount(selectedAccount.id);
                } else if (normalizedAccounts.length > 1 && consumePendingAccountSelection()) {
                    // Multi-account user just completed a fresh login. Bootstrap provisionally
                    // wrote accounts[0] to the session, but the user hasn't made an explicit
                    // selection yet — send them to the account selector.
                    setSelectedAccountIdState(null);
                } else {
                    // Restored from server session — update local state only, cookie is still valid.
                    setSelectedAccountIdState(selectedAccount.id);
                }
            } else if (normalizedAccounts.length === 1) {
                await selectAccount(normalizedAccounts[0].id);
            } else {
                setSelectedAccountIdState(null);
            }
        } catch (error: unknown) {
            setAccounts([]);
            setSelectedAccountIdState(null);
            if (axios.isAxiosError(error) && error.response) {
                setErrorStatus(error.response.status);
            } else {
                setErrorStatus(500);
            }

            if (axios.isAxiosError(error) && error.response?.status === 401) {
                void invalidateWebsiteSession();
            }
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isInitialized, normalizeAccountId, resetAccountState, selectAccount]);

    // Auth/lookup/file hook that sits outside the React Query data layer by design.
    // react-doctor-disable-next-line no-fetch-in-effect
    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (isAuthenticated) {
            fetchAccounts();
            return;
        }

        if (hasSession !== false) {
            void axios.delete('/api/sessions', { skipAuth: true }).catch(() => {
                // Session cleanup best-effort when auth context is no longer available.
            });
        }

        resetAccountState();
    }, [fetchAccounts, hasSession, isAuthenticated, isInitialized, resetAccountState]);

    const selectedAccount = useMemo(
        () => accounts.find(account => account.id === selectedAccountId) ?? null,
        [accounts, selectedAccountId]
    );

    return {
        accounts,
        selectedAccount,
        selectedAccountId,
        isPrimaryAccount: selectedAccount?.isPrimary ?? false,
        isLoading,
        isReady: !isLoading,
        errorStatus,
        hasAccounts: accounts.length > 0,
        fetchAccounts,
        selectAccount,
    };
};
